import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import Layout from '../components/layout/Layout';
import ResponsiveTable from '../components/common/ResponsiveTable';
import ResponsiveSelect from '../components/common/ResponsiveSelect';
import DebouncedSearchInput from '../components/common/DebouncedSearchInput';
import { useInfiniteGateEntries, useAssignDock } from '../hooks/useGateEntries';
import { GateEntry } from '../types';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../hooks/useConfirm';
import { useAuth } from '../contexts/AuthContext';
import GateEntryPrintView from '../components/inward-outward/GateEntryPrintView';

type StatusFilter = 'all' | 'pending_dock_assignment' | 'dock_assigned' | 'grn_created';

export default function Inward() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { ConfirmDialog } = useConfirm();
  const { hasPermission, user } = useAuth();
  // Edit of an approved (past) GRN — permission-based, and always for Admin / Super Admin.
  const isAdmin = ['ADMIN', 'SUPER_ADMIN'].includes((user?.role?.name || '').toUpperCase());
  const canEditPastGrn = hasPermission('grn:edit_past') || isAdmin;
  const [searchTerm, setSearchTerm] = useState('');
  const [searchOpen, setSearchOpen] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [dockNumbers, setDockNumbers] = useState<Record<string, string>>({});
  const [printEntry, setPrintEntry] = useState<GateEntry | null>(null);
  const [highlightedId, setHighlightedId] = useState<number | string | null>(null);
  const highlightedRowRef = useRef<HTMLTableRowElement>(null);
  const hasProcessedState = useRef(false);

  // Fetch inward gate entries with filters
  const filters = useMemo(() => ({
    limit: 20,
    search: searchTerm || undefined,
    shipmentType: 'inward' as const,
    status: statusFilter !== 'all' ? statusFilter : undefined,
  }), [searchTerm, statusFilter]);

  const {
    data: gateEntriesData,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteGateEntries(filters);

  const assignDockMutation = useAssignDock();

  const getErrorMessage = (error: any, fallback: string) => {
    if (error?.errors && Array.isArray(error.errors)) {
      return error.errors.join(', ');
    }
    return error?.message || fallback;
  };

  // Handle search from debounced input
  const handleSearch = useCallback((query: string) => {
    setSearchTerm(query);
  }, []);

  // Flatten all pages of data
  const gateEntries = useMemo(() => {
    if (!gateEntriesData) return [];
    return gateEntriesData.pages.flatMap(page => page.data?.items || []);
  }, [gateEntriesData]);

  // Get total count from backend API response
  const totalEntriesCount = useMemo(() => {
    if (!gateEntriesData || !gateEntriesData.pages || gateEntriesData.pages.length === 0) return 0;
    // Get total from the first page's metadata
    return gateEntriesData.pages[0].data?.meta?.total || 0;
  }, [gateEntriesData]);

  // Get stats from backend API response metadata (if available)
  const stats = useMemo(() => {
    if (!gateEntriesData || !gateEntriesData.pages || gateEntriesData.pages.length === 0) {
      return { pending: 0, assigned: 0, grnCreated: 0 };
    }
    
    const firstPageMeta = gateEntriesData.pages[0].data?.meta;
    
    // If backend provides status counts in metadata, use them
    if (firstPageMeta?.statusCounts) {
      return {
        pending: firstPageMeta.statusCounts.pending_dock_assignment || 0,
        assigned: firstPageMeta.statusCounts.dock_assigned || 0,
        grnCreated: firstPageMeta.statusCounts.grn_created || 0,
      };
    }
    
    // Fallback: calculate from loaded data (less accurate for filtered views)
    const pending = gateEntries.filter(e => e.status === 'pending_dock_assignment').length;
    const assigned = gateEntries.filter(e => e.status === 'dock_assigned').length;
    const grnCreated = gateEntries.filter(e => e.status === 'grn_created').length;
    
    return { pending, assigned, grnCreated };
  }, [gateEntriesData, gateEntries]);

  // Handle dock assignment
  const handleAssignDock = async (entry: GateEntry) => {
    const dockNumber = dockNumbers[entry.id];
    
    if (!dockNumber || !dockNumber.trim()) {
      toast.error('Please enter a dock number');
      return;
    }

    try {
      await assignDockMutation.mutateAsync({
        id: entry.id.toString(),
        dockNumber: dockNumber.trim(),
      });
      
      // Clear the dock number input
      setDockNumbers(prev => {
        const updated = { ...prev };
        delete updated[entry.id];
        return updated;
      });
      
      toast.success(`Dock ${dockNumber} assigned successfully`);
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Failed to assign dock'));
    }
  };

  // Handle create GRN - navigate to GRN page
  const handleCreateGRN = (entry: GateEntry) => {
    navigate(`/grn-create/${entry.id}`);
  };

  // Editing an approved GRN opens a full duplicate-of-Inward edit page —
  // every change is staged locally there and only submitted as one bundle;
  // inventory/storage stay untouched until an Admin/Super Admin approves it.
  const handleEditPastGrn = (entry: GateEntry) => {
    if (!entry.grnId) return;
    navigate(`/grn-past-edit/${entry.grnId}`);
  };

  // Update dock number input
  const handleDockNumberChange = (entryId: string | number, value: string) => {
    setDockNumbers(prev => ({
      ...prev,
      [entryId]: value,
    }));
  };

  // Hide body scrollbar on mount, restore on unmount
  useEffect(() => {
    document.body.classList.add('scrollbar-hide');
    document.documentElement.classList.add('scrollbar-hide');

    return () => {
      document.body.classList.remove('scrollbar-hide');
      document.documentElement.classList.remove('scrollbar-hide');
    };
  }, []);

  // Handle selected entry from navigation state (from Security Check)
  useEffect(() => {
    const state = location.state as { selectedEntry?: GateEntry; message?: string } | null;
    if (state?.selectedEntry && !hasProcessedState.current) {
      hasProcessedState.current = true;
      const entryId = state.selectedEntry.id;
      
      // Invalidate and refetch gate entries to get latest data
      queryClient.invalidateQueries({ queryKey: ['gateEntries'] });
      
      setHighlightedId(entryId);
      
      // Show message if provided
      if (state.message) {
        toast.success(state.message);
      }
      
      // Clear navigation state immediately to prevent re-triggering
      navigate(location.pathname, { replace: true, state: {} });
      
      // Scroll to highlighted row after a short delay to ensure rendering
      setTimeout(() => {
        highlightedRowRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }, 500);
      
      // Clear highlight after 3 seconds
      setTimeout(() => {
        setHighlightedId(null);
      }, 3000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Loading state
  if (isLoading) {
    return (
      <Layout>
        <div className="min-h-screen px-1 py-6 lg:p-6 bg-gradient-to-br from-gray-50 via-brand-50/30 to-gray-50 flex items-center justify-center">
          <div className="text-center bg-white p-10 rounded-2xl shadow-2xl border border-gray-200">
            <div className="relative">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-brand-200 border-t-blue-600 mx-auto mb-6"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <svg className="w-8 h-8 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                </svg>
              </div>
            </div>
            <p className="text-gray-800 font-semibold text-lg">Loading inward entries...</p>
            <p className="text-gray-500 text-sm mt-2">Please wait while we fetch your data</p>
          </div>
        </div>
      </Layout>
    );
  }

  // Error state
  if (isError) {
    return (
      <Layout>
        <div className="min-h-screen px-1 py-6 lg:p-6 bg-gray-50 flex items-center justify-center">
          <div className="text-center bg-white p-10 rounded-2xl shadow-2xl border-2 border-red-200">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-red-600 font-semibold text-lg mb-2">Failed to load inward entries</p>
            <p className="text-gray-600 text-sm mb-4">{getErrorMessage(error, 'Something went wrong')}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl hover:from-primary-700 hover:to-primary-800 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              Refresh Page
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen px-3 py-4 lg:p-4 bg-gradient-to-br from-gray-50 to-gray-100 scrollbar-hide" style={{ overflowY: 'auto' }}>
        {/* Header with enhanced shadow and blue tint */}
        <div className="mb-4">
          <div className="flex items-center space-x-3 bg-gradient-to-r from-brand-100 to-brand-50 p-4 sm:p-5 rounded-xl shadow-lg border-2 border-brand-300">
            <div className="p-2.5 sm:p-3 bg-gradient-to-br from-primary-600 to-primary-700 rounded-xl shadow-md flex-shrink-0">
              <svg className="w-6 h-6 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
              </svg>
            </div>
            <div className="min-w-0">
              <h2 className="text-xl sm:text-3xl font-bold text-gray-900">Inbound Process</h2>
              <p className="text-gray-600 mt-0.5 sm:mt-1 text-sm sm:text-base">Dock assignment and GRN generation for inbound shipments</p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-4">
          <div className="bg-white rounded-xl p-4 sm:p-6 border-2 border-gray-200 shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-gray-600">Pending Dock Assignment</p>
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-xl flex items-center justify-center shadow-md">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <p className="text-4xl font-bold text-gray-900">{stats.pending}</p>
          </div>

          <div className="bg-white rounded-xl p-4 sm:p-6 border-2 border-gray-200 shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-gray-600">Dock Assigned</p>
              <div className="w-12 h-12 bg-gradient-to-br from-brand-500 to-brand-600 rounded-xl flex items-center justify-center shadow-md">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
            </div>
            <p className="text-4xl font-bold text-gray-900">{stats.assigned}</p>
          </div>

          <div className="bg-white rounded-xl p-4 sm:p-6 border-2 border-gray-200 shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-gray-600">GRN Created</p>
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-md">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
            <p className="text-4xl font-bold text-gray-900">{stats.grnCreated}</p>
          </div>
        </div>

        {/* Inbound Entries Table */}
        <div className="bg-white rounded-2xl shadow-xl border-2 border-gray-300 overflow-hidden">
          <div className="p-4 sm:p-5 bg-gradient-to-r from-gray-50 to-white border-b-2 border-gray-300">
            <div className="flex items-center min-w-0">
              <svg className="w-6 h-6 text-primary-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                Inbound Entries
                {totalEntriesCount > 0 && (
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    ({totalEntriesCount} {totalEntriesCount === 1 ? 'entry' : 'entries'})
                  </span>
                )}
              </h3>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="p-4 sm:p-5 bg-gray-50 border-b-2 border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
              {searchOpen ? (
                <DebouncedSearchInput
                  placeholder="Search by entry number, vehicle, driver, or client..."
                  onSearch={handleSearch}
                  debounceMs={300}
                  onClose={() => { setSearchOpen(false); handleSearch(''); }}
                />
              ) : (
                <button
                  onClick={() => setSearchOpen(true)}
                  className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:bg-gray-50 hover:border-gray-400 transition-all text-sm font-medium"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  Search
                </button>
              )}
              
              <ResponsiveSelect
                value={statusFilter}
                onChange={(val) => setStatusFilter((val || 'all') as StatusFilter)}
                includeEmpty={false}
                placeholder="All Status"
                className="px-4 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white shadow-sm transition-all duration-200 hover:border-gray-400"
                options={[
                  { value: 'all', label: 'All Status' },
                  { value: 'pending_dock_assignment', label: 'Pending Dock Assignment' },
                  { value: 'dock_assigned', label: 'Dock Assigned' },
                  { value: 'grn_created', label: 'GRN Created' },
                ]}
              />
            </div>
          </div>

          <div className="px-3 py-3 lg:p-5">
            <ResponsiveTable
            columns={[
              {
                key: 'entryNumber',
                label: 'Entry Number',
                width: '140px',
                render: (value) => (
                  <div className="font-medium text-gray-900" title={value}>
                    {value}
                  </div>
                )
              },
              {
                key: 'createdAt',
                label: 'Date',
                width: '100px',
                render: (value) => (
                  <div className="text-sm text-gray-900">
                    {value ? new Date(value).toLocaleDateString() : '-'}
                  </div>
                )
              },
              {
                key: 'vehicleNumber',
                label: 'Vehicle',
                width: '120px',
                render: (value) => (
                  <div className="font-medium text-gray-900" title={value}>
                    {value}
                  </div>
                )
              },
              {
                key: 'driverName',
                label: 'Driver',
                width: '130px',
                render: (value) => (
                  <div className="max-w-[130px] truncate" title={value || ''}>
                    {value || '-'}
                  </div>
                )
              },
              {
                key: 'clientName',
                label: 'Client',
                width: '180px',
                render: (value, row) => (
                  <div className="max-w-[180px] flex items-center gap-1.5">
                    <span className="truncate" title={value || ''}>{value || '-'}</span>
                    {row.hasLbd && (
                      <span
                        className="px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-orange-100 text-orange-800 border border-orange-300 whitespace-nowrap"
                        title="This inward contains LBD (damaged) items stored separately"
                      >
                        LBD
                      </span>
                    )}
                  </div>
                )
              },
              {
                key: 'invoiceNumber',
                label: 'Invoice',
                width: '120px',
                render: (value) => (
                  <div className="text-sm max-w-[120px] truncate" title={value || ''}>
                    {value || '-'}
                  </div>
                )
              },
              {
                key: 'dockNumber',
                label: 'Dock',
                width: '80px',
                render: (value) => (
                  <div className="font-medium text-gray-900">
                    {value || '-'}
                  </div>
                )
              },
              {
                key: 'status',
                label: 'Status',
                width: '150px',
                render: (value, entry) => {
                  // A reopened Past GRN takes precedence in the badge.
                  if (entry.reopenedForEdit) {
                    return (
                      <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full whitespace-nowrap bg-amber-100 text-amber-800">
                        Past GRN Resubmitted
                      </span>
                    );
                  }
                  // If GRN exists, show GRN status
                  if (entry.grnStatus) {
                    const statusConfig: Record<string, { bg: string; text: string; label: string }> = {
                      draft: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'GRN Draft' },
                      submitted: { bg: 'bg-brand-100', text: 'text-brand-800', label: 'GRN Submitted' },
                      approved: { bg: 'bg-green-100', text: 'text-green-800', label: 'GRN Approved' },
                      rejected: { bg: 'bg-red-100', text: 'text-red-800', label: 'GRN Rejected' },
                      cancelled: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'GRN Cancelled' },
                    };
                    const config = statusConfig[entry.grnStatus] || statusConfig.draft;
                    return (
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full whitespace-nowrap ${config.bg} ${config.text}`}>
                        {config.label}
                      </span>
                    );
                  }
                  
                  // Otherwise show gate entry status
                  return (
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full whitespace-nowrap
                      ${value === 'pending_dock_assignment' ? 'bg-yellow-100 text-yellow-800' : 
                        value === 'dock_assigned' ? 'bg-brand-100 text-brand-800' :
                        value === 'grn_created' ? 'bg-green-100 text-green-800' : 
                        'bg-gray-100 text-gray-800'}`}
                    >
                      {value === 'pending_dock_assignment' ? 'Pending Dock' :
                       value === 'dock_assigned' ? 'Dock Assigned' :
                       value === 'grn_created' ? 'GRN Created' :
                       value}
                    </span>
                  );
                }
              },
              {
                key: 'actions',
                label: 'Actions',
                width: '220px',
                render: (_, entry) => (
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Case 1: Pending Dock Assignment - Show Assign Dock */}
                    {entry.status === 'pending_dock_assignment' && (
                      <>
                        <input
                          type="text"
                          placeholder="Dock #"
                          className="w-20 min-h-[36px] px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                          value={dockNumbers[entry.id] || ''}
                          onChange={(e) => handleDockNumberChange(entry.id, e.target.value)}
                          disabled={assignDockMutation.isPending}
                        />
                        <button
                          onClick={() => handleAssignDock(entry)}
                          disabled={assignDockMutation.isPending}
                          className="px-3 py-2 min-h-[36px] bg-brand-600 text-white text-xs rounded hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {assignDockMutation.isPending ? 'Assigning...' : 'Assign Dock'}
                        </button>
                      </>
                    )}

                    {/* Case 2: Dock Assigned - Show Create GRN */}
                    {entry.status === 'dock_assigned' && (
                      <button
                        onClick={() => handleCreateGRN(entry)}
                        className="px-3 py-2 min-h-[36px] bg-green-600 text-white text-xs rounded hover:bg-green-700 flex items-center"
                      >
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Create GRN
                      </button>
                    )}
                    
                    {/* Case 3: GRN Created - Open GRN */}
                    {entry.status === 'grn_created' && (
                      <button
                        onClick={() => handleCreateGRN(entry)}
                        className="px-3 py-2 min-h-[36px] bg-brand-600 text-white text-xs rounded hover:bg-brand-700 flex items-center"
                      >
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Open GRN
                      </button>
                    )}

                    {/* Edit past GRN — only once the GRN is approved, for permitted users / admins.
                        Opens a Past GRN edit-request form (submitted for Admin approval). */}
                    {entry.grnStatus === 'approved' && canEditPastGrn && entry.grnId && (
                      <button
                        onClick={() => handleEditPastGrn(entry)}
                        className="px-3 py-2 min-h-[36px] bg-brand-50 text-brand-700 text-xs rounded border border-brand-300 hover:bg-brand-100 disabled:opacity-50 flex items-center"
                      >
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Edit
                      </button>
                    )}

                    {/* Available regardless of status (draft, pending, approved,
                        etc.) as long as the user holds grn:print (Admin/Super
                        Admin always get it, same as canEditPastGrn above). */}
                    {(hasPermission('grn:print') || isAdmin) && (
                      <button
                        onClick={() => setPrintEntry(entry)}
                        className="px-3 py-2 min-h-[36px] bg-gray-100 text-gray-700 text-xs rounded border border-gray-300 hover:bg-gray-200 flex items-center"
                        title="Print entry details"
                      >
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m10-10V5a2 2 0 00-2-2H9a2 2 0 00-2 2v2m10 0H7m10 0v10a2 2 0 01-2 2H9a2 2 0 01-2-2V7" />
                        </svg>
                        Print
                      </button>
                    )}
                  </div>
                )
              }
            ]}
            data={gateEntries}
            isLoading={isFetchingNextPage}
            onLoadMore={() => fetchNextPage()}
            hasMore={hasNextPage}
            height="600px"
            emptyMessage="No inward entries found. New entries will appear here from the security gate."
            highlightedRowId={highlightedId}
            getRowRef={(rowId) => rowId === highlightedId ? highlightedRowRef : null}
          />
          </div>
        </div>
      </div>
      <ConfirmDialog />
      {printEntry && <GateEntryPrintView entry={printEntry} onClose={() => setPrintEntry(null)} />}
    </Layout>
  );
}
